import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { MessageSquare, Brain, Users, Navigation, CreditCard, CheckCircle } from 'lucide-react';

const steps = [
  {
    number: '01',
    title: 'Request Initiated',
    description: 'Patient or rider speaks or types their transportation request through any channel.',
    icon: MessageSquare,
    visual: 'speech-bubble',
    color: 'cyan',
  },
  {
    number: '02',
    title: 'AI Conversation',
    description: 'OVA AI confirms trip details, special requirements, and preferences through natural conversation.',
    icon: Brain,
    visual: 'ai-interface',
    color: 'purple',
  },
  {
    number: '03',
    title: 'Smart Matching',
    description: 'AI selects the best driver based on proximity, credentials, vehicle type, and availability.',
    icon: Users,
    visual: 'matching',
    color: 'blue',
  },
  {
    number: '04',
    title: 'Live Trip Execution',
    description: 'Real-time navigation, AI camera monitoring, and instant updates throughout the journey.',
    icon: Navigation,
    visual: 'navigation',
    color: 'green',
  },
  {
    number: '05',
    title: 'Completion + Payment',
    description: 'Automated billing to insurance or direct payment, with instant payout to drivers.',
    icon: CreditCard,
    visual: 'payment',
    color: 'cyan',
  },
];

const StepCard = ({ step, index }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "center center"]
  });
  
  const opacity = useTransform(scrollYProgress, [0, 0.5, 1], [0.3, 1, 1]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.95, 1, 1]);
  const x = useTransform(scrollYProgress, [0, 0.5, 1], [index % 2 === 0 ? -50 : 50, 0, 0]);

  const colorClasses = {
    cyan: 'border-neon-cyan/30 text-neon-cyan',
    purple: 'border-neon-purple/30 text-neon-purple',
    blue: 'border-neon-blue/30 text-neon-blue',
    green: 'border-neon-green/30 text-neon-green',
  };

  return (
    <motion.div
      ref={ref}
      style={{ opacity, scale, x }}
      className={`relative flex items-center ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 lg:gap-16`}
    >
      {/* Content Card */}
      <div className="flex-1 glass-card p-6 lg:p-8 group hover:border-white/20 transition-all duration-500">
        <div className="flex items-start space-x-4">
          {/* Step Number */}
          <div className={`flex-shrink-0 w-12 h-12 rounded-full border-2 ${colorClasses[step.color]} flex items-center justify-center font-mono text-sm font-bold`}>
            {step.number}
          </div>
          
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-3">
              <step.icon className={`w-5 h-5 ${colorClasses[step.color].split(' ')[1]}`} />
              <h3 className="text-xl lg:text-2xl font-heading font-bold text-white">
                {step.title}
              </h3>
            </div>
            <p className="text-slate-400 leading-relaxed">
              {step.description}
            </p>
          </div>
        </div>

        {/* Visual representation */}
        <div className="mt-6 p-4 bg-black/30 rounded-lg border border-white/5">
          <StepVisual step={step} />
        </div>
      </div>

      {/* Timeline Connector (visible on lg) */}
      <div className="hidden lg:flex flex-col items-center">
        <div className={`w-4 h-4 rounded-full ${colorClasses[step.color].split(' ')[0]} border-2 ${colorClasses[step.color]}`} />
        {index < steps.length - 1 && (
          <div className="w-px h-32 bg-gradient-to-b from-white/20 to-transparent" />
        )}
      </div>

      {/* Empty space for alternating layout */}
      <div className="hidden lg:block flex-1" />
    </motion.div>
  );
};

const StepVisual = ({ step }) => {
  switch (step.visual) {
    case 'speech-bubble':
      return (
        <div className="flex items-end space-x-3">
          <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center">
            <span className="text-xs">U</span>
          </div>
          <div className="bg-slate-800 rounded-2xl rounded-bl-none px-4 py-2 max-w-xs">
            <p className="text-sm text-slate-300">"I need a ride to my dialysis appointment tomorrow at 9am"</p>
          </div>
        </div>
      );
    case 'ai-interface':
      return (
        <div className="space-y-3">
          <div className="flex items-end justify-end space-x-3">
            <div className="bg-neon-cyan/20 rounded-2xl rounded-br-none px-4 py-2 max-w-xs border border-neon-cyan/30">
              <p className="text-sm text-neon-cyan">"I found your appointment at Metro Dialysis Center. Do you need wheelchair assistance?"</p>
            </div>
            <div className="w-8 h-8 rounded-full bg-neon-cyan/20 flex items-center justify-center">
              <Brain className="w-4 h-4 text-neon-cyan" />
            </div>
          </div>
        </div>
      );
    case 'matching':
      return (
        <div className="flex items-center justify-between">
          <div className="text-center">
            <div className="w-12 h-12 rounded-full bg-slate-700 mx-auto mb-2 flex items-center justify-center">
              <Users className="w-6 h-6 text-slate-400" />
            </div>
            <p className="text-xs text-slate-500">3 drivers available</p>
          </div>
          <div className="flex-1 mx-4 h-px bg-gradient-to-r from-slate-700 via-neon-blue to-slate-700 relative">
            <motion.div 
              className="absolute top-1/2 -translate-y-1/2 w-2 h-2 bg-neon-blue rounded-full"
              animate={{ x: [0, 100, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
          </div>
          <div className="text-center">
            <div className="w-12 h-12 rounded-full bg-neon-blue/20 mx-auto mb-2 flex items-center justify-center border border-neon-blue/30">
              <CheckCircle className="w-6 h-6 text-neon-blue" />
            </div>
            <p className="text-xs text-neon-blue">Best match found</p>
          </div>
        </div>
      );
    case 'navigation':
      return (
        <div className="relative h-24 overflow-hidden rounded-lg bg-slate-900">
          {/* Simplified map visualization */}
          <div className="absolute inset-0">
            <svg className="w-full h-full" viewBox="0 0 200 80">
              <path 
                d="M 20 60 Q 50 20 100 40 T 180 30" 
                fill="none" 
                stroke="#0f172a" 
                strokeWidth="20"
              />
              <path 
                d="M 20 60 Q 50 20 100 40 T 180 30" 
                fill="none" 
                stroke="#10b981" 
                strokeWidth="3"
                strokeDasharray="5,5"
              />
              <circle cx="20" cy="60" r="6" fill="#06b6d4" />
              <circle cx="180" cy="30" r="6" fill="#10b981" />
              <motion.circle 
                cx="20" 
                cy="60" 
                r="4" 
                fill="white"
                animate={{ 
                  cx: [20, 100, 180],
                  cy: [60, 40, 30]
                }}
                transition={{ duration: 3, repeat: Infinity }}
              />
            </svg>
          </div>
          <div className="absolute bottom-2 left-2 text-xs font-mono text-slate-500">
            ETA: 12 min
          </div>
        </div>
      );
    case 'payment':
      return (
        <div className="flex items-center justify-around">
          <div className="text-center">
            <div className="text-2xl font-heading font-bold text-white">$47.50</div>
            <p className="text-xs text-slate-500">Trip Total</p>
          </div>
          <div className="flex flex-col items-center">
            <motion.div
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 1, repeat: Infinity }}
            >
              <CheckCircle className="w-8 h-8 text-neon-green" />
            </motion.div>
            <p className="text-xs text-neon-green mt-1">Paid</p>
          </div>
          <div className="text-center">
            <div className="text-2xl font-heading font-bold text-neon-green">$38.75</div>
            <p className="text-xs text-slate-500">Driver Payout</p>
          </div>
        </div>
      );
    default:
      return null;
  }
};

export const HowItWorksSection = () => {
  return (
    <section 
      id="how-it-works" 
      className="relative py-24 lg:py-32 overflow-hidden"
      data-testid="how-it-works-section"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-medex-bg via-medex-surface/30 to-medex-bg" />
      
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <span className="inline-block px-4 py-1 rounded-full bg-white/5 border border-white/10 text-sm font-mono text-slate-400 uppercase tracking-wider mb-6">
            How It Works
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-white mb-6">
            From Request to Completion
          </h2>
          <p className="max-w-2xl mx-auto text-lg text-slate-400">
            Every trip is a seamless flow of AI-powered automation, from the first word to the final payment.
          </p>
        </motion.div>

        {/* Steps */}
        <div className="space-y-12 lg:space-y-0">
          {steps.map((step, index) => (
            <StepCard key={step.number} step={step} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
